import React from 'react';
import { Button } from '@/components/ui/button';
import { Check, RotateCcw, X } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { toast } from 'sonner';

interface FloorPlanEditControlsProps {
  floorPlanId: string;
  onUndo?: () => void;
  onReset?: () => void;
  onSave?: () => Promise<void>;
}

export function FloorPlanEditControls({ 
  floorPlanId,
  onUndo,
  onReset,
  onSave 
}: FloorPlanEditControlsProps) {
  const setEditingFloorPlanImage = useAppStore((state) => state.setEditingFloorPlanImage);
  
  const handleDone = async () => {
    try {
      if (onSave) {
        await onSave();
      }
      setEditingFloorPlanImage(false);
      toast.success('Floor plan updated');
    } catch (error) {
      toast.error('Failed to save floor plan changes');
    }
  };
  
  const handleUndo = () => {
    if (onUndo) {
      onUndo();
      toast.info('Reverted to previous position');
    }
  };
  
  const handleReset = () => {
    if (onReset) {
      onReset();
      toast.info('Reset to original position');
    }
  };
  
  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-lg border p-4 z-50">
      <div className="flex items-center gap-2">
        <div className="text-sm font-medium mr-4">
          Editing Floor Plan
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleUndo}
          className="flex items-center gap-2"
          title="Undo last change"
        >
          <RotateCcw className="h-4 w-4" />
          Undo
        </Button>
        
        <Button
          variant="outline"
          size="sm"
          onClick={handleReset}
          className="flex items-center gap-2"
          title="Reset to original position"
        >
          <X className="h-4 w-4" />
          Reset
        </Button>
        
        <Button
          variant="default"
          size="sm"
          onClick={handleDone}
          className="flex items-center gap-2 ml-2"
        >
          <Check className="h-4 w-4" />
          Done Editing
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground mt-2 text-center">
        Drag to move • Drag handles to resize • Press ESC to cancel
      </div>
    </div>
  );
}